/**
 * Which edit bounding boxes confine the active module.
 *
 * Each extension declares its edit-region domain via
 * `ExtensionHandler.getEditRegionDomain` (label_raster: its overlay domain;
 * vector: the opt-in `edit_region_domain`); this file only dispatches through
 * the registry, so the workspace never branches on `template_type`.
 */
import { extensionRegistry } from "@shared/core/index"
import { editBboxesForDomain, type SeriesEditBbox, sortEditBboxes } from "@shared/lib/edit-bbox"

export interface EditRegionModule {
  template_type: string
  template_schema: string
  config: string | null
}

/** One row of a module panel's Regions card. */
export interface EditRegionItem {
  key: string
  label: string
  note: string | null
  width: number
  height: number
}

function parseJson(text: string | null): unknown {
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** Edit-region domain of a module, or null when it never uses edit regions. */
export function getModuleEditRegionDomain(module: EditRegionModule | null): string | null {
  if (!module) return null
  const handler = extensionRegistry.getHandler(module.template_type)
  if (!handler?.getEditRegionDomain) return null
  return handler.getEditRegionDomain(parseJson(module.template_schema), parseJson(module.config))
}

/**
 * Boxes of the active slide that apply to `domain`, sorted for display /
 * cycling. `null` = unrestricted (no domain, or the slide has no matching box).
 */
export function resolveEditRegions(
  bboxes: readonly SeriesEditBbox[] | null | undefined,
  domain: string | null
): SeriesEditBbox[] | null {
  if (!(bboxes && domain)) return null
  const matching = editBboxesForDomain(bboxes, domain)
  return matching.length > 0 ? sortEditBboxes(matching) : null
}

export function toEditRegionItems(regions: readonly SeriesEditBbox[]): EditRegionItem[] {
  return regions.map((bbox, index) => ({
    key: bbox.roi_key,
    label: bbox.label?.trim() || `Region ${index + 1}`,
    note: bbox.note,
    width: bbox.width,
    height: bbox.height
  }))
}
